(ns hai.contacts.detail
  "Full-page view of a single contact as the shared editor surface — the same
   one the create modal shows: the composer with its Save and Cancel, and the
   header ⋮ holding Delete. Mirrors [[hai.notes.detail]]."
  (:require
   [hai.contacts.data :as data]
   [hai.contacts.editor :as editor]
   [hai.contacts.form :as form]
   [hai.entity.draft :as draft]
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.web-app.viewport :as viewport]
   [lambdaisland.ornament :as o]))

(defsub ::window-title
  [db]
  (let [contact-uuid (get-in db [:routing/current-route :path-params :contact/uuid])
        contact      (when contact-uuid (get-in db [:contact/uuid (uuid (str contact-uuid))]))]
    (or (not-empty (:contact/full-name contact)) "Contact")))

(defn draft-id
  "The draft behind one contact's page. Per contact, so opening a second one
   in another tab doesn't edit the first one's."
  [contact-uuid]
  [::draft (uuid (str contact-uuid))])

;; Seeded when the route's resolve lands and re-seeded by Cancel; the route's
;; :stop discards it.
(defevent ::seed-draft [db contact-uuid]
  (draft/seed db
              (draft-id contact-uuid)
              (form/from-contact (get-in db [:contact/uuid (uuid (str contact-uuid))]))))

(defevent ::discard-draft [db contact-uuid]
  (draft/discard db (draft-id contact-uuid)))

;; Tracks which contacts a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both leave a nil entity.
(defevent ::mark-resolved [db contact-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str contact-uuid))))

(defsub ::resolved?
  [db contact-uuid]
  (boolean (and contact-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str contact-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

;; The dialog caps the modal surface and floats it over a dimmed page; here the
;; column mirrors the cap and a shadow provides the elevation the overlay would.
(o/defstyled editor-column :div
  {:max-width "760px"}
  [editor/edit-modal
   {:box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 12px 32px rgba(45,62,42,0.16)"}])

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn- contact-view [contact]
  (let [contact-uuid (:contact/uuid contact)
        mobile?      @(<s [::viewport/mobile?])
        saving?      (= :start @(<s [:fetch/state [::data/save-edit contact-uuid]]))
        delete!      (fn []
                       (>e [::data/delete contact-uuid])
                       (>e [:routing/navigate [:contacts/view]]))
        editor-props {:title       "Editing contact"
                      :contact     contact
                      :draft-id    (draft-id contact-uuid)
                      :auto-focus? false
                      :show-hint?  (not mobile?)
                      :saving?     saving?
                      ;; The page reads the contact from the entity store,
                      ;; where the save's response lands.
                      :on-save     (fn [staged] (>e [::data/save-edit contact-uuid staged]))
                      ;; Cancel puts the draft back to the contact as last saved.
                      :on-cancel   #(>e [::seed-draft contact-uuid])
                      :on-close    #(>e [:routing/navigate [:contacts/view]])
                      :on-delete   (when (sharing/owner? contact) delete!)}]
    (if mobile?
      ;; The same full-screen surface the create flow expands into; ✕ returns
      ;; to the contacts page.
      [editor/screen editor-props]
      [editor-column
       [editor/modal-content editor-props]])))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        contact   @(<s [:entity params])
        draft     @(<s [::draft/draft (draft-id (:contact/uuid params))])
        resolved? @(<s [::resolved? (:contact/uuid params)])]
    [layout/page
     [layout/content
      (cond
        ;; The composer edits the draft the route seeded, and stays put across
        ;; saves — so a landing save never interrupts the typing. Reopening a
        ;; contact finds it in the entity store before the resolve has
        ;; re-seeded the draft (leaving discarded it), so the draft is what
        ;; the page waits for.
        (and contact draft)
        [contact-view contact]

        (and resolved? (nil? contact))
        [muted "This contact doesn't exist or you no longer have access to it."]

        :else
        [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
